import {
  Box,
  Card,
  CardBody,
  Flex,
  Link,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react"
import ProductPrice from "@components/Product/ProductPrice"
import Ratings from "@components/Product/Ratings"
import TurboBadge from "@components/Product/TurboBadge"
import { addBusinessDays, convertDateFormat, formatDate } from "@utils/date"
import { blurDataURLFromThumbHash } from "@utils/image"
import { offersOffsets } from "@utils/offers"
import { Hit } from "instantsearch.js"
import Image from "next/image"
import NextLink from "next/link"
import { useMemo } from "react"
import { Highlight } from "react-instantsearch"

type HitProps = {
  hit: Hit
}

const HitComponent = ({ hit }: HitProps) => {
  const { offers: reOffers, fastOffset } = useMemo(() => {
    return offersOffsets(hit.offers?.offers || [])
  }, [hit.offers?.offers])

  const offers = [...reOffers].reverse()

  const comingSoon = hit.datePublished
    ? new Date(
        convertDateFormat(String(hit.datePublished), {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      ) > new Date()
    : false

  const outOfStock = !(
    offers &&
    offers.length > 0 &&
    offers[0].availability !== "OutOfStock"
  )

  const fastDeliveryMinDate =
    offers?.length > 0
      ? addBusinessDays({
          numDays: offers[0].deliveryLeadTime.minValue + fastOffset,
        })
      : null

  const fastDeliveryMaxDate =
    offers?.length > 0
      ? addBusinessDays({
          numDays: offers[0].deliveryLeadTime.maxValue + fastOffset,
        })
      : new Date()

  const fastSameMonth =
    fastDeliveryMinDate?.getMonth() === fastDeliveryMaxDate?.getMonth()

  const fastDeliveryMin = fastDeliveryMinDate
    ? formatDate(fastDeliveryMinDate, {
        day: "numeric",
        month: fastSameMonth ? undefined : "short",
      })
    : null
  const fastDeliveryMax = fastDeliveryMaxDate
    ? formatDate(fastDeliveryMaxDate, { day: "numeric", month: "short" })
    : null

  return (
    <Card
      w={"full"}
      bgColor={useColorModeValue("white", "blackAlpha.300")}
    >
      <CardBody p={4}>
        <Link
          as={NextLink}
          href={`/p/${hit.alternateName.en_AE}/${hit.id}`}
          _hover={{
            textDecoration: "none",
          }}
        >
          <Flex alignItems={"flex-start"}>
            {hit.image?.length > 0 ? (
              <Image
                alt={hit.name.en_AE}
                src={`${process.env.NEXT_PUBLIC_IMAGE_CDN}${hit.image[0].contentUrl}?class=msearch`}
                width={hit.image[0].width.value}
                height={hit.image[0].height.value}
                loader={({ src }) => src}
                unoptimized
                placeholder={"blur"}
                blurDataURL={blurDataURLFromThumbHash(
                  hit.image[0].exifData.value
                )}
                style={{
                  minWidth: "96px",
                  maxWidth: "96px",
                }}
              />
            ) : (
              <Image
                alt={hit.name.en_AE}
                src={`${process.env.NEXT_PUBLIC_IMAGE_CDN}/b-img/${hit.gtin}.png?class=msearch`}
                width={800}
                height={1200}
                placeholder={"blur"}
                blurDataURL={blurDataURLFromThumbHash(
                  "JCoGDQDPmHd4enfoWIyGeMNwaQ2H"
                )}
                unoptimized
                style={{
                  minWidth: "96px",
                  maxWidth: "96px",
                }}
              />
            )}

            <VStack
              fontSize={"md"}
              fontWeight={"500"}
              px={2}
              py={0}
              mt={-1}
              spacing={1.5}
              alignItems={"flex-start"}
            >
              <Box lineHeight={1.2}>
                <Highlight
                  attribute={"name.en_AE"}
                  hit={hit}
                />
              </Box>

              {hit.author?.en_AE && (
                <Box fontSize={"xs"}>
                  {"By "}
                  <Box
                    as={"span"}
                    fontWeight={"500"}
                    fontStyle={"italic"}
                  >
                    <Highlight
                      attribute={"author.en_AE"}
                      hit={hit}
                      separator={", "}
                    />
                  </Box>
                </Box>
              )}

              {hit.bookFormat?.en_AE && (
                <Box fontSize={"xs"}>
                  <Highlight
                    attribute={"bookFormat.en_AE"}
                    hit={hit}
                  />
                </Box>
              )}

              {hit.audience.description?.en_AE && (
                <Box fontSize={"xs"}>{hit.audience.description.en_AE}</Box>
              )}

              {hit.aggregateRating?.ratingValue > 0 && (
                <Ratings
                  stars={hit.aggregateRating?.ratingValue}
                  count={hit.aggregateRating?.ratingCount}
                />
              )}

              {!outOfStock && !comingSoon ? (
                <>
                  <ProductPrice
                    listPrice={Number(hit.offers.priceSpecification?.price)}
                    price={Number(hit.offers.lowPrice)}
                    discountValue={Number(
                      hit.offers?.offers[0]?.identifier?.value
                    )}
                    currency={hit.offers.priceCurrency}
                  />

                  {offers[0]?.deliveryLeadTime?.maxValue <= 3 && <TurboBadge />}

                  {offers?.length > 0 && (
                    <Flex
                      fontSize={"xs"}
                      fontWeight={700}
                      alignItems={"flex-start"}
                    >
                      <Box
                        as={"span"}
                        fontWeight={700}
                      >
                        {"Free Delivery"}
                      </Box>
                      <Box
                        as={"span"}
                        fontWeight={500}
                        ms={1}
                      >
                        {fastDeliveryMin}
                        {" - "}
                        {fastDeliveryMax}
                      </Box>
                    </Flex>
                  )}
                </>
              ) : (
                <Box
                  fontSize={"xs"}
                  color={comingSoon ? "black" : "red.500"}
                >
                  {comingSoon
                    ? `Releasing on ${convertDateFormat(
                        String(hit.datePublished),
                        { day: "numeric", month: "short", year: "numeric" }
                      )}`
                    : "Temporarily out of stock"}
                </Box>
              )}
            </VStack>
          </Flex>
        </Link>
      </CardBody>
    </Card>
  )
}

export default HitComponent
